import type { GeneralSummaryListItems, SummaryListItemType } from '@/types'
import { formatAddress, formatDate } from '@/helpers/format'
import { getRegionForCanton } from '@/helpers/region'
import { Canton } from '@/schemas/ch/enums'

export type TranslationFn = (key: string) => string
export type WidgetTextFn = (...path: string[]) => string

export interface GeneralSummaryInput {
  projectGeneral: Record<string, unknown> | null
}

const LEGACY_TASK_FIELDS = [
  'buildingPlan',
  'rawConstruction',
  'facadeEnvelope',
  'interiors',
  'electricInstallation',
  'heatingVentilation',
  'sanitary',
] as const

function hasLegacyTaskFields(pg: Record<string, unknown> | null): boolean {
  if (!pg) return false
  return LEGACY_TASK_FIELDS.some((field) => field in pg)
}

function hasPerUsageTaskFields(pg: Record<string, unknown> | null): boolean {
  if (!pg || !Array.isArray(pg.usageItems)) return false
  return pg.usageItems.some((usage) => {
    if (!usage || typeof usage !== 'object') return false
    return 'buildingPlan' in usage
  })
}

function getLegacyBuildingPlanSummaryValue(pg: Record<string, unknown> | null, wt: WidgetTextFn): string {
  if (!pg) return ''
  const legacyBuildingPlan = pg.buildingPlan as string | undefined
  if (!legacyBuildingPlan) return ''
  return wt('requirementsAndTasks', 'task', 'planType', 'option', legacyBuildingPlan)
}

export function renderGeneralSummary(
  input: GeneralSummaryInput,
  t: TranslationFn,
  wt: WidgetTextFn,
  wtSummary: WidgetTextFn
): GeneralSummaryListItems {
  const pg = input.projectGeneral
  const shouldShowLegacyTaskInGeneralSummary = hasLegacyTaskFields(pg) && !hasPerUsageTaskFields(pg)
  const legacyBuildingPlanSummaryValue = getLegacyBuildingPlanSummaryValue(pg, wt)

  const generalList: SummaryListItemType[] = [
    {
      title: wtSummary('pdf', 'project', 'general', 'description'),
      value: (pg?.description as string) ?? '',
      additionalClass: 'text-block',
    },
    {
      title: wtSummary('pdf', 'project', 'general', 'created'),
      value: pg?.created ? formatDate(pg.created as string) : '',
    },
    {
      title: wtSummary('pdf', 'project', 'general', 'address'),
      value: pg ? formatAddress(pg) : '',
    },
    ...(pg?.canton
      ? [
          {
            title: wtSummary('pdf', 'project', 'general', 'region'),
            value: wtSummary('pdf', 'project', 'general', 'regions', getRegionForCanton(pg.canton as Canton)),
          },
        ]
      : []),
    {
      title: wtSummary('pdf', 'project', 'general', 'status'),
      value: pg?.status ? wt('details', 'status', 'option', pg.status as string) : '',
    },
    ...(shouldShowLegacyTaskInGeneralSummary
      ? [
          {
            title: wtSummary('pdf', 'project', 'setup', 'usage', 'buildingPlan'),
            value: legacyBuildingPlanSummaryValue,
          } as SummaryListItemType,
        ]
      : []),
  ]

  const organisationList: SummaryListItemType[] = [
    {
      title: wtSummary('pdf', 'project', 'organisation', 'planing'),
      value: pg?.planningSideOption
        ? wt('organisation', 'planningSideOptions', 'option', pg.planningSideOption as string)
        : '',
    },
    {
      title: wtSummary('pdf', 'project', 'organisation', 'company'),
      value: pg?.companySideOption
        ? wt('organisation', 'companySideOptions', 'option', pg.companySideOption as string)
        : '',
    },
  ]

  return { generalList, organisationList }
}
